/**
 * BRE-7889: the emoji a comment can be reacted with, shared by every comment
 * surface. Rows store the stable `key`, never the glyph: glyph variants (skin
 * tones, VS16) would split counts, and a key survives a glyph change.
 *
 * Adding an emoji is an edit here only: the column is plain text with no CHECK
 * or enum, and the write boundary closes the set with `z.enum(COMMENT_REACTION_KEYS)`.
 * Removing one hides existing reactions with it (readers drop unknown keys).
 */
export const COMMENT_REACTIONS = [
  { key: "thumbs_up", emoji: "👍", label: "Thumbs up" },
  { key: "heart", emoji: "❤️", label: "Heart" },
  { key: "tada", emoji: "🎉", label: "Celebrate" },
  { key: "eyes", emoji: "👀", label: "Looking" },
  { key: "check", emoji: "✅", label: "Done" },
  { key: "laugh", emoji: "😂", label: "Laugh" },
] as const;

export type CommentReaction = (typeof COMMENT_REACTIONS)[number];
export type CommentReactionKey = CommentReaction["key"];

export const COMMENT_REACTION_KEYS = COMMENT_REACTIONS.map((r) => r.key) as [
  CommentReactionKey,
  ...CommentReactionKey[],
];

export const DEFAULT_QUICK_REACTION: CommentReactionKey = "thumbs_up";

/** Reactor names sent per chip, for its tooltip; the rest are counted. */
export const COMMENT_REACTION_REACTOR_NAME_CAP = 10;

const BY_KEY: ReadonlyMap<string, CommentReaction> = new Map(
  COMMENT_REACTIONS.map((r) => [r.key, r]),
);

export function isCommentReactionKey(value: unknown): value is CommentReactionKey {
  return typeof value === "string" && BY_KEY.has(value);
}

/** Undefined for a key this build's registry lacks: keys arrive over the wire
 *  from a server that may have a newer registry than a long-open tab. */
export function getCommentReaction(key: string): CommentReaction | undefined {
  return BY_KEY.get(key);
}

/** One emoji's reactions on one comment, as the viewer sees them. */
export interface CommentReactionSummaryDto {
  key: CommentReactionKey;
  count: number;
  /** The viewer is one of the reactors (drives the pressed state). */
  reactedByMe: boolean;
  /** Display names, oldest reaction first, at most `COMMENT_REACTION_REACTOR_NAME_CAP`. */
  reactorNames: string[];
}
